// The Arabic dictionary.
//
// Two rules run through it. Technical terms a lifter reads as symbols are left
// in English - 1RM, RPE, e1RM, PDF, XLSX, API, S3 - because translating them
// loses the thing they name: an athlete looking for "RPE" on the screen is
// looking for those three letters. And the app's own name, StrongFish, is a
// name rather than a word.
//
// The layout follows the text: `dir="rtl"` is set on the document when this
// locale is picked (see I18nContext), which is what mirrors the sidebar, the
// tables and the form fields.
const ar = {
  app: {
    name: "StrongFish",
    tagline: "تطبيق رفع الأثقال",
  },
  common: {
    cm: "سم",
    retry: "إعادة التحميل",
    create: "إنشاء",
    copy: "نسخ",
    copied: "تم النسخ إلى الحافظة.",
    save: "حفظ",
    cancel: "إلغاء",
    delete: "حذف",
    duplicate: "تكرار",
    edit: "تعديل",
    add: "إضافة",
    close: "إغلاق",
    refresh: "تحديث",
    confirm: "تأكيد",
    search: "بحث",
    noResults: "لا نتائج",
    selectAll: "تحديد الكل",
    clearSelection: "مسح",
    selectedOne: "عنصر واحد محدد",
    selectedCount: "{{count}} محددة",
    selectPlaceholder: "اختر...",
    loading: "جارٍ التحميل...",
    none: "لا شيء",
    yes: "نعم",
    no: "لا",
    back: "رجوع",
    actions: "إجراءات",
    optional: "اختياري",
    unknown: "؟",
    kg: "كغ",
    all: "الكل",
    open: "فتح",
    logout: "تسجيل الخروج",
    language: "اللغة",
    theme: "المظهر",
    light: "فاتح",
    dark: "داكن",
    required: "مطلوب",
  },
  nav: {
    myPrograms: "برامجي",
    strength: "القوة",
    find: "بحث",
    more: "المزيد",
    feed: "المستجدات",
    training: "تدريبي",
    oneRms: "أرقامي القصوى (1RM)",
    healthData: "بيانات الصحة",
    clubs: "الأندية",
    exercises: "التمارين",
    profile: "الملف الشخصي",
    admin: "الإدارة",
    settings: "الإعدادات",
    about: "حول التطبيق",
    events: "التقويم",
    documentation: "التوثيق",
    search: "البحث عن أشخاص",
    messages: "الرسائل",
    coach: "مدرب الذكاء الاصطناعي",
    invitations: "الدعوات",
    contact: "اتصل بنا",
    apiKeys: "مفاتيح API",
    downloadApp: "تحميل تطبيق Android",
    downloadAppScan: "امسح هذا الرمز بهاتفك لتحميل التطبيق.",
    collapseSidebar: "طيّ القائمة",
    expandSidebar: "توسيع القائمة",
  },
  auth: {
    welcomeBack: "أهلاً بعودتك",
    nameOrUsername: "أدخل اسمك ولقبك، أو اختر اسم مستخدم — أحدهما يكفي.",
    accountType: "أنا...",
    imAnAthlete: "رياضي",
    imAnAthleteHelp: "تتبع البرامج التي يكتبها مدربك.",
    imACoach: "مدرب",
    imACoachHelp: "تكتب البرامج وتدير الأندية. يؤكد ذلك مشرف عام أولاً.",
    coachRequestSent: "حساب المدرب في انتظار التأكيد. يمكنك بالفعل استخدام StrongFish كرياضي.",
    scanQr: "سجّل الدخول بمسح رمز QR",
    scanQrHelp: "افتح صفحة مفاتيح API في تطبيق الويب، أنشئ مفتاحاً، ثم امسح رمز QR الذي يظهر.",
    scanFailed: "هذا الرمز لا يحمل إعدادات StrongFish.",
    login: "تسجيل الدخول",
    signup: "إنشاء حساب",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    confirmPassword: "تأكيد كلمة المرور",
    name: "الاسم",
    surname: "اسم العائلة",
    forgotPassword: "نسيت كلمة المرور؟",
    resetPassword: "إعادة تعيين كلمة المرور",
    newPassword: "كلمة مرور جديدة",
    sendResetLink: "أرسل لي رابطاً",
    resetLinkSent: "إذا كان هذا البريد مسجلاً، فقد أُرسل رابط إعادة التعيين.",
    passwordUpdated: "تم تحديث كلمة المرور. يمكنك تسجيل الدخول الآن.",
    noAccount: "ليس لديك حساب بعد؟",
    hasAccount: "لديك حساب بالفعل؟",
    orContinueWith: "أو تابع عبر",
    confirmed: "تم تأكيد حسابك. يمكنك تسجيل الدخول.",
    confirmFailed: "رابط التأكيد هذا غير صالح أو انتهت صلاحيته.",
    confirmBanned: "تم حظر هذا الحساب من قِبل الإدارة.",
    checkEmail: "تحقق من بريدك: أرسلنا لك رابطاً لتأكيد حسابك.",
    mfaTitle: "المصادقة بعاملين",
    mfaCode: "رمز المصادقة",
    mfaCodeHelp: "أدخل الرمز المكوّن من 6 أرقام من تطبيق المصادقة.",
    mfaUseKey: "استخدام مفتاح الأمان",
    mfaVerify: "تحقق",
    heroTitle: "درّب بالأرقام، لا بالتخمين.",
    heroSubtitle: "برامج مبنية على RPE تتبع أحمالها رقمك الأقصى 1RM الحالي - يكتبها مدربك، وتُحتسب من جديد من أجلك.",
    photoCreditBy: "رفعة الميت في بطولة أوروبا IPF. الصورة بعدسة",
    accountCreated: "أهلًا بك في StrongFish، حسابك جاهز.",
  },
  profile: {
    measures: "عنك",
    height: "الطول",
    title: "الملف الشخصي العام",
    gender: "الجنس",
    genderMale: "ذكر",
    genderFemale: "أنثى",
    username: "اسم المستخدم",
    handleHelp: "يُشارَك ملفك على /profile/{{handle}}",
    anonymous: "التصفّح باسم المستخدم",
    anonymousHelp: "يرى الأعضاء الآخرون اسم المستخدم بدل اسمك في كل مكان - منشوراتك ورسائلك وأنديتك. اسمك محفوظ، لكنه مخفي.",
    anonymousNeedsUsername: "اختر اسم مستخدم أولاً: هو الاسم الذي سيراه الناس بدل اسمك.",
    bio: "نبذة",
    bodyweight: "وزن الجسم",
    visibility: "من يرى ملفي",
    visibilityPublic: "الجميع",
    comments: "{{count}} تعليق",
    visibilityClubs: "أنديتي",
    visibilityPrivate: "مدرّبيّ فقط",
    visibilityHelp: {
      public: "يمكن لأي شخص لديه الرابط رؤية ملفك وأرقامك القصوى ومنشوراتك العامة.",
      clubs: "لا يرى ملفك إلا من يشاركك نادياً.",
      private: "لا يرى ملفك إلا مدربو ومشرفو أنديتك.",
    },
    birthdate: "تاريخ الميلاد",
    birthdateHelp: "بعد تحديده، يظهر عيد ميلادك في تقويم زملاء النادي الذين يرون ملفك.",
    notVisible: "هذا الملف غير مرئي لك.",
    bests: "أفضل الأرقام",
    total: "المجموع",
    followers: "المتابِعون",
    following: "يتابع",
    follow: "متابعة",
    unfollow: "إلغاء المتابعة",
    clubs: "الأندية",
    posts: "المنشورات",
    noPosts: "لا منشورات بعد.",
    notFound: "هذا الملف غير موجود، أو ليس عاماً.",
    memberSince: "عضو منذ {{date}}",
    coach: "مدرب",
    athlete: "رياضي",
    superadmin: "مشرف عام",
    socials: "أين تجدني أيضاً",
    socialsHelp: "حسابك على كل منها - أو الصق عنوان ملفك، وسيُستخرج الاسم منه.",
    rank: "الترتيب",
    avatar: "الصورة الشخصية",
    changeAvatar: "تغيير الصورة",
    saved: "تم حفظ الملف الشخصي.",
  },
  mfa: {
    title: "المصادقة بعاملين",
    subtitle: "احمِ حسابك بعامل ثانٍ.",
    totp: "تطبيق المصادقة",
    totpEnabled: "تطبيق المصادقة مُفعّل",
    totpDisabled: "لا يوجد تطبيق مصادقة مسجّل",
    enableTotp: "تفعيل",
    disableTotp: "تعطيل",
    scanQr: "امسح رمز QR هذا بتطبيق المصادقة، ثم أدخل الرمز الذي يعرضه.",
    manualSecret: "أو أدخل هذا المفتاح يدوياً:",
    securityKeys: "مفاتيح الأمان",
    securityKeysHelp: "مفاتيح USB مثل YubiKey، أو المصادق المدمج في جهازك.",
    addKey: "إضافة مفتاح أمان",
    keyName: "اسم المفتاح",
    noKeys: "لا يوجد مفتاح أمان مسجّل",
    keyAdded: "تم تسجيل مفتاح الأمان.",
    keyRemoved: "تم حذف مفتاح الأمان.",
    totpConfirmed: "المصادقة بعاملين مُفعّلة.",
    totpRemoved: "تم حذف تطبيق المصادقة.",
    unsupported: "هذا المتصفح لا يدعم مفاتيح الأمان.",
  },
  clubs: {
    title: "الأندية",
    create: "إنشاء نادٍ",
    edit: "تعديل النادي",
    name: "الاسم",
    description: "الوصف",
    city: "المدينة",
    country: "البلد",
    logo: "الشعار",
    members: "الأعضاء",
    memberCount: "{{count}} عضواً",
    owner: "المالك",
    admin: "مشرف",
    member: "عضو",
    role: "الدور",
    addMember: "إضافة عضو",
    addMemberHelp: "ابحث بالبريد الإلكتروني أو اسم الملف أو الاسم الكامل. يجب أن يكون للشخص حساب بالفعل.",
    invite: "دعوة شخص",
    inviteHelp: "يصله بريد فيه رابط. إن لم يكن لديه حساب بعد، تنتظر الدعوة حتى ينشئ حساباً بهذا العنوان.",
    inviteMessage: "كلمة لهم",
    sendInvitation: "إرسال الدعوة",
    invitationSent: "تم إرسال الدعوة.",
    pendingInvitations: "في انتظار الرد",
    withdrawInvitation: "سحب الدعوة",
    promote: "ترقية إلى مشرف",
    demote: "إعادة إلى عضو",
    remove: "إزالة من النادي",
    leave: "مغادرة هذا النادي",
    transfer: "نقل الملكية",
    transferHelp: "يجب أن يكون المالك الجديد عضواً بالفعل. ستصبح أنت مشرفاً.",
    empty: "لست في أي نادٍ بعد.",
    emptyCoach: "أنشئ ناديك الأول لتبدأ التدريب.",
    created: "تم إنشاء النادي.",
    saved: "تم حفظ النادي.",
    deleted: "تم حذف النادي.",
    memberAdded: "تمت إضافة العضو.",
    memberRemoved: "تمت إزالة العضو.",
    confirmDelete: "حذف النادي \"{{name}}\"؟ ستُحذف برامجه ومنشوراته أيضاً.",
    confirmRemove: "إزالة {{name}} من النادي؟",
    confirmLeave: "مغادرة النادي \"{{name}}\"؟",
    feed: "مستجدات النادي",
    feedback: "الملاحظات",
  },
  programs: {
    pickMembers: "اختر أعضاء",
    assignCount: "إسناد إلى {{count}}",
    mine: "برامجي",
    mineSubtitle: "الدورات التي كتبتها لنفسك.",
    mineEmptyTitle: "لا برامج خاصة بك بعد",
    mineEmptyBody: "اكتب واحداً، أو انسخ برنامجاً من أحد أنديتك وعدّله.",
    new: "برنامج جديد",
    exportPdf: "ورقة PDF",
    export: "تصدير",
    exportXlsx: "جدول Excel ‏(.xlsx)",
    exportPng: "صورة للمشاركة ‏(.png)",
    exportWeek: "تصدير هذا الأسبوع",
    exportBlock: "تصدير الدورة كاملة",
    exporting: "جارٍ التحضير...",
    copy: "نسخ",
    copied: "تم نسخ البرنامج",
    move: "نقل",
    moved: "تم نقل البرنامج",
    moveInstead: "انقله بدل نسخه",
    copyHelp: "للنسخة حصصها الخاصة، فتعديلها لا يمسّ الأصل.",
    destination: "إلى أين",
    moveNeedsElsewhere: "النقل يحتاج وجهة أخرى: البرنامج موجود هنا بالفعل.",
    pickDestination: "اختر نادياً",
    myLibrary: "برامجي الخاصة",
    visibility: "من يراه",
    visibilityPrivate: "أنا فقط",
    visibilityPrivateHelp: "لا يمكن لأحد غيرك فتحه.",
    visibilityClubs: "أنديتي",
    visibilityClubsHelp: "يمكن لمن تتدرب معهم فتحه.",
    visibilityPublic: "أي شخص لديه الرابط",
    visibilityPublicHelp: "قابل للمشاركة خارج التطبيق.",
    importOptional: "استيراد جدول (اختياري)",
    title: "البرامج",
    import: "استيراد جدول",
    importHelp:
      "ارفع ملف ‎.xlsx‎ الخاص بالمدرب. يُقرأ تخطيطان تلقائياً: ورقة لكل أسبوع، تبدأ كل حصة بصف عناوين \"Exercice | Reps | RPE | Percentage | Load\"؛ أو ورقة لكل دورة، بعناوين \"EXERCICES | SÉRIES/REPS | RPE\" وعمود W1..Wn لكل أسبوع. تُتجاهل أي أوزان يحسبها الملف بنفسه - فالأحمال تُحتسب دائماً من الأرقام القصوى الخاصة بكل رياضي.",
    importing: "جارٍ الاستيراد...",
    imported: "تم استيراد البرنامج: {{weeks}} أسابيع، {{days}} حصص، {{sets}} مجموعات.",
    createdExercises: "تمارين جديدة أُضيفت إلى الكتالوج: {{list}}",
    referenceOneRms: "كُتب هذا الملف لهذه الأرقام القصوى:",
    useAsMyOneRms: "اعتمدها كأرقامي القصوى",
    warnings: "ملاحظات عن هذا الملف",
    programName: "اسم البرنامج",
    name: "الاسم",
    description: "الوصف",
    empty: "لا برنامج في هذا النادي بعد.",
    weeks: "{{count}} أسابيع",
    sessions: "{{count}} حصص",
    setCount: "{{count}} مجموعات",
    author: "بقلم {{name}}",
    week: "الأسبوع {{week}}",
    public: "مشارَك علناً",
    private: "خاص",
    publicHelp: "يمكن لأي شخص لديه الرابط قراءة هذا البرنامج، دون حساب.",
    privateHelp: "لا يقرأ هذا البرنامج إلا أعضاء هذا النادي.",
    publish: "مشاركة علنية",
    unpublish: "جعله خاصاً",
    published: "أصبح هذا البرنامج عاماً. يمكن لأي شخص لديه الرابط قراءته.",
    unpublished: "عاد البرنامج خاصاً. لم يعد الرابط المشارَك يعمل.",
    copyLink: "نسخ الرابط",
    day: "اليوم {{day}}",
    assign: "إسناد",
    assignTo: "إسناد هذا البرنامج",
    assigned: "تم إسناد البرنامج.",
    assignmentsSaved: "تم تحديث الإسنادات.",
    unassign: "إلغاء الإسناد",
    assignments: "مُسند إلى",
    noAssignments: "غير مُسند إلى أحد بعد.",
    startDate: "تاريخ البدء",
    note: "ملاحظة للرياضي",
    deleted: "تم حذف البرنامج.",
    confirmDelete: "حذف البرنامج \"{{name}}\"؟ سيُحذف معه كل إسناد وكل مجموعة مسجّلة.",
    saved: "تم حفظ البرنامج.",
    viewAs: "العرض بصفة",
    myself: "أنا",
    create: "إنشاء برنامج",
    createHelp: "ابدأ فارغاً وأضف الحصص، أو استورد جدول المدرب.",
    created: "تم إنشاء البرنامج.",
    addSession: "إضافة حصة",
    editSession: "تعديل الحصة",
    sessionSaved: "تم حفظ الحصة.",
    sessionDeleted: "تم حذف الحصة.",
    confirmDeleteSession: "حذف الأسبوع {{week}} اليوم {{day}} ومجموعاته؟",
    noSessions: "لا حصة بعد. أضف الأولى.",
    addSet: "إضافة مجموعة",
    duplicateSet: "تكرار هذه المجموعة",
    duplicateSession: "نسخ هذه الحصة ومجموعاتها إلى اليوم نفسه من الأسبوع التالي المتاح",
    reorderSession: "اسحب لإعادة ترتيب هذه الحصة",
    dropSetHere: "أفلت مجموعة هنا",
    reorderHelp: "اسحب المجموعة من المقبض لإعادة ترتيبها، أو إلى حصة أخرى لنقلها إليها. وتُنقل الحصص بالطريقة نفسها، من المقبض بجانب عنوانها.",
    editSet: "تعديل المجموعة",
    setSaved: "تم حفظ المجموعة.",
    setDeleted: "تم حذف المجموعة.",
    confirmDeleteSet: "حذف هذه المجموعة؟",
    edit: "تعديل البرنامج",
    done: "انتهيت من التعديل",
    buildMode: "تعديل البرنامج",
  },
  session: {
    aiFeedbackAgain: "تحليل آخر",
    aiFeedback: "تحليل الذكاء الاصطناعي",
    aiFeedbackHelp: "اطلب من الذكاء الاصطناعي قراءة هذه الحصة مقارنة بالحصص السابقة.",
    aiFeedbackLocked: "أكمل الحصة - بتسجيل كل المجموعات - لطلب التحليل.",
    exercise: "التمرين",
    reps: "التكرارات",
    rpe: "RPE",
    percentage: "%",
    load: "الحمل",
    onTheBar: "على البار",
    e1rm: "e1RM",
    part: "الجزء",
    from1rm: "من رقمك الأقصى 1RM في {{lift}} البالغ {{value}} كغ",
    fromTodaysE1rm: "من الـ{{value}} كغ التي أظهرتها للتو في هذه الحركة، لا من 1RM المسجّل",
    perceivedRpe: "الإحساس",
    exportWeek: "تصدير هذا الأسبوع",
    exportSession: "تصدير هذه الحصة",
    exportBlock: "تصدير الدورة",
    markDone: "وضع علامة: منجزة",
    markUndone: "وضع علامة: غير منجزة",
    markDayDone: "اعتبار الحصة كلها منجزة",
    markDayUndone: "اعتبار الحصة كلها غير منجزة",
    rpePlaceholder: "RPE",
    autoregulated: "أُعيد حسابه مما رفعته للتو",
    bodyweight: "وزن الجسم",
    beltless: "بدون حزام",
    beltlessQuestion: "بدون حزام؟",
    absolute: "حمل ثابت",
    missingOneRm: "أدخل رقمك الأقصى 1RM لرؤية الأحمال",
    missingOneRmsList: "الأحمال ناقصة لـ: {{list}}",
    setMyOneRms: "إدخال أرقامي القصوى",
    log: "تسجيل هذه المجموعة",
    logged: "تم تسجيل المجموعة.",
    actualReps: "التكرارات المنجزة",
    actualRpe: "RPE المُحسّ",
    actualLoad: "الحمل المستخدم",
    loggedLoad: "الحمل الذي سجّلته لهذه المجموعة.",
    loggedLoadPlanned: "الحمل الذي سجّلته لهذه المجموعة. المُخطَّط: {{value}}.",
    loggedReps: "التكرارات التي سجّلتها لهذه المجموعة.",
    loggedRepsPlanned: "التكرارات التي سجّلتها لهذه المجموعة. المُخطَّط: {{value}}.",
    comment: "تعليق لمدربك",
    done: "منجزة",
    clearLog: "مسح",
    yourE1rm: "رقمك الأقصى التقديري لهذه المجموعة: {{value}} كغ",
    beatsMax: "هذا أعلى من رقمك الأقصى المسجّل.",
    progress: "{{done}} / {{total}} مجموعات منجزة",
    status: "الحالة",
    statusActive: "قيد التنفيذ",
    statusDone: "منتهٍ",
    statusArchived: "مؤرشف",
    noAssignments: "لم يُسند إليك أي برنامج بعد.",
    noAssignmentsHelp: "يُسند مدربك البرامج من ناديك.",
    loadMode: "كيف يُحسب الحمل؟",
    loadModeRpe: "تكرارات عند RPE (من 1RM)",
    loadModePercentage: "نسبة مئوية من 1RM",
    loadModeAbsolute: "وزن ثابت",
    loadModeBodyweight: "وزن الجسم",
    loadModeHelp: "وصفة RPE تُحتسب من الرقم الأقصى الخاص بكل عضو؛ أما النسبة المئوية فتُطبَّق كما كُتبت.",
    absoluteLoad: "الوزن",
    notes: "ملاحظات",
    week: "الأسبوع",
    day: "اليوم",
    title: "العنوان",
  },
  oneRms: {
    title: "أرقامي القصوى (1RM)",
    subtitle: "كل حمل موصوف يُحتسب من هذه الأرقام. حدّث واحداً منها ويُعاد حساب برنامجك كله.",
    lift: "الحركة",
    value: "1RM",
    updated: "آخر تحديث",
    history: "السجل",
    save: "حفظ",
    saved: "تم تحديث 1RM.",
    removed: "تم حذف 1RM.",
    empty: "لا رقم أقصى مسجّل بعد.",
    addLift: "إضافة حركة",
    mainLifts: "حركات المنافسة",
    otherLifts: "حركات أخرى",
    confirmDelete: "حذف رقمك الأقصى في {{name}}؟",
  },
  exercises: {
    title: "كتالوج التمارين",
    subtitle: "مشترك بين كل المدربين. أضف حركة مرة واحدة ليتمكن الجميع من برمجتها.",
    create: "إضافة تمرين",
    edit: "تعديل التمرين",
    name: "الاسم",
    labelEn: "الاسم (بالإنجليزية)",
    labelFr: "الاسم (بالفرنسية)",
    aliases: "أسماء بديلة",
    aliasesHelp: "مفصولة بفواصل. الجداول التي تستخدم هذه الكتابات ستُستورد على هذا التمرين.",
    category: "الفئة",
    categorySquat: "السكوات",
    categoryBench: "ضغط البنش",
    categoryDeadlift: "رفعة الميت",
    categoryAccessory: "تمرين مساعد",
    oneRmRef: "تُحتسب الأحمال من",
    oneRmRefHelp: "أي رقم أقصى من حركات المنافسة تُحتسب منه وصفة النسبة أو RPE.",
    oneRmRefNone: "بلا رقم أقصى (حمل ثابت)",
    bodyweight: "يُحمَّل بوزن الجسم",
    created: "تمت إضافة التمرين.",
    saved: "تم حفظ التمرين.",
    deleted: "تم حذف التمرين.",
    confirmDelete: "حذف \"{{name}}\" من الكتالوج؟",
    empty: "لا تمرين مطابق.",
    main: "حركة منافسة",
    mainHelp: "يُطلب من الأعضاء تسجيل 1RM لها، وتُحتسب أحمال الحركات المشتقة منها. للمشرف العام فقط.",
    adminOnly: "لا يمكن إلا لمشرف عام تعديل أو حذف مدخل في الكتالوج - فهو مشترك بين كل الأندية.",
    usageWarning: "حذف {{name}} سيحذف أيضاً {{sets}} مجموعة موصوفة في {{programs}} برامج، و{{oneRms}} رقماً أقصى مسجّلاً. لا يمكن التراجع عن ذلك.",
    usageNone: "{{name}} غير مستخدم في أي برنامج بعد.",
    checkingUsage: "جارٍ التحقق مما سيتأثر...",
  },
  feed: {
    title: "المستجدات",
    discover: "استكشاف",
    following: "المتابَعون",
    compose: "شارك شيئاً مع ناديك...",
    post: "نشر",
    posted: "تم النشر.",
    visibility: "الظهور",
    visibilityPublic: "الجميع",
    visibilityClub: "النادي فقط",
    pickClub: "اختر نادياً",
    pickClubs: "اختر أندية",
    addPicture: "إضافة صورة",
    addVideo: "إضافة فيديو",
    uploadingVideo: "جارٍ رفع الفيديو... {{percent}}%",
    like: "إعجاب",
    liked: "أعجبني",
    comment: "تعليق",
    comments: "{{count}} تعليق",
    editedAt: "عُدّل في {{date}}",
    ownPostLikes: "الإعجابات بمنشورك. لا يمكنك الإعجاب بمنشورك.",
    writeComment: "اكتب تعليقاً...",
    empty: "لا شيء هنا بعد. تابع أحداً، أو انضم إلى نادٍ.",
    emptyDiscover: "لا منشور عام بعد.",
    edited: "مُعدّل",
    report: "إبلاغ",
    reported: "تم الإبلاغ. سينظر فيه مشرف.",
    reportReason: "لماذا تبلّغ عن هذا؟",
    reportSpam: "رسائل مزعجة",
    reportAbuse: "إساءة أو تحرّش",
    reportInappropriate: "محتوى غير لائق",
    reportOther: "أخرى",
    reportComment: "هل تريد إضافة شيء؟",
    confirmDeletePost: "حذف هذا المنشور؟",
    confirmDeleteComment: "حذف هذا التعليق؟",
    postDeleted: "تم حذف المنشور.",
    commentDeleted: "تم حذف التعليق.",
    postUnavailable: "هذا المنشور غير متاح. ربما حُذف، أو شورك مع نادٍ فقط.",
    joinToReply: "انضم إلى StrongFish للإعجاب بهذا المنشور والرد عليه.",
    likes: "{{count}} إعجاب",
  },
  admin: {
    title: "الإدارة",
    users: "المستخدمون",
    clubs: "الأندية",
    reports: "الإشراف",
    coachRequests: "طلبات التدريب",
    searchUsers: "البحث في الحسابات",
    makeRole: "تعيينه {{role}}",
    ips: "عناوين الاتصال",
    ipsTitle: "اتصالات {{name}}",
    ipsHelp: "كل عنوان اتصل منه هذا الحساب، وعدد المرات. حجب عنوان مهمة الجدار الناري، لا هذه الشاشة.",
    ip: "العنوان",
    ipCount: "الاتصالات",
    ipFirstSeen: "أول ظهور",
    ipLastSeen: "آخر ظهور",
    noIpsTitle: "لا اتصال مسجّل",
    noIpsBody: "تُسجَّل العناوين عند تسجيل دخول هذا الحساب.",
    confirmCoach: "تأكيد",
    rejectCoach: "رفض",
    rejectCoachTitle: "رفض طلب التدريب هذا؟",
    rejectCoachHelp: "يُرسَل سببك إلى {{name}} بالبريد، فاكتبه لهم.",
    rejectCoachPlaceholder: "لم نتمكن من التحقق من مؤهلاتك التدريبية...",
    coachApproved: "أصبح هذا الحساب مدرباً.",
    coachRejected: "رُفض الطلب وأُبلغ صاحبه بالسبب.",
    noCoachRequestsTitle: "لا مدرب في الانتظار",
    noCoachRequestsBody: "عندما يسجّل أحدهم كمدرب، يظهر طلبه هنا.",
    stats: "نظرة عامة",
    totalUsers: "الحسابات",
    openReports: "بلاغات مفتوحة",
    role: "الدور",
    roleSuperadmin: "مشرف عام",
    roleCoach: "مدرب",
    roleConfirmed: "رياضي",
    roleDisabled: "قيد الانتظار",
    roleBan: "محظور",
    editUser: "تعديل الحساب",
    newPassword: "كلمة مرور جديدة (اتركها فارغة للإبقاء)",
    clearMfa: "إعادة تعيين المصادقة بعاملين",
    clearMfaHelp: "يحذف كل عامل ثانٍ، لمستخدم فقد هاتفه ومفتاحه.",
    mfaCleared: "أُعيد تعيين المصادقة بعاملين.",
    userSaved: "تم حفظ الحساب.",
    userDeleted: "تم حذف الحساب.",
    confirmDeleteUser: "حذف {{email}}؟ ستُحذف معه أنديته وبرامجه ومنشوراته.",
    reportStatus: "الحالة",
    reportOpen: "مفتوح",
    reportResolved: "مُعالَج",
    reportDismissed: "مرفوض",
    reportedBy: "أبلغ عنه",
    reportTarget: "المحتوى",
    resolveAndDelete: "حذف المحتوى",
    dismiss: "تجاهل",
    resolve: "معالجة",
    resolved: "تمت معالجة البلاغ.",
    noReports: "لا شيء للإشراف عليه.",
  },
  devices: {
    lift: { squat: "القرفصاء", bench: "الضغط", deadlift: "الرفعة المميتة" },
    pair: {
      password: "كلمة مرور Stance",
      stance: { email: "بريد Stance" },
    },
    record: "تسجيل مجموعة",
    recordHint: "يفتح تطبيق Stance لتتبع مجموعة، ثم يعرضها هنا.",
    recordFailed: "تعذّر فتح تطبيق Stance. هل هو مثبّت؟",
    barSpeed: {
      title: "سرعة البار - آخر مجموعة",
      empty: "لا توجد مجموعة متتبعة بعد. سجّل واحدة في تطبيق Stance لتظهر هنا.",
      load: "{{weight}} كغ × {{reps}}",
      rpePair: "RPE {{given}} محسوس، {{real}} مقاس",
      rpeReal: "RPE مقاس {{real}}",
      rep: "تكرار {{n}}",
    },
    provider: {
      "google-health": "Google Health ‏(Fitbit، Pixel Watch)",
      withings: "Withings",
      stance: "Stance Fitness",
    },
    aiFeedbackWindow: "على مدى {{days}} يوماً",
    aiFeedback: "تحليل الذكاء الاصطناعي",
    aiFeedbackAsk: "اطلب التحليل",
    aiFeedbackAgain: "اطلب من جديد",
    aiFeedbackHelp: "قراءة لآخر 90 يوماً: النشاط ونبض القلب وتكوين الجسم.",
    aiFeedbackEmpty: "لا يوجد تحليل بعد. يمكن للذكاء الاصطناعي قراءة آخر 90 يوماً متى شئت.",
    title: "الأجهزة المتصلة",
    subtitle: "اقرأ ما قاسته ساعتك وميزانك بالفعل. لا يُكتب شيء في المقابل، ولا يُخزَّن شيء هنا.",
    connect: "ربط",
    connectGoogle: "الاتصال عبر Google",
    connectGoogleHint: "أكمِل تسجيل الدخول عبر Google في تطبيق الويب.",
    disconnect: "إلغاء الربط",
    notConnected: "غير مرتبط.",
    unavailable: "غير مفعّل على هذا الخادم.",
    connectedSince: "مرتبط منذ {{date}}.",
    total: "إجمالي الخطوات",
    dailyAverage: "المعدل لكل 24 ساعة",
    perDay: "يوماً بيوم",
    averageBpm: "متوسط معدل ضربات القلب",
    peakBpm: "أعلى معدل لضربات القلب",
    noHeartRate: "لم تُسجَّل أي ضربات قلب في هذه الفترة.",
    heartRangeTooWide: "لا تعود بيانات نبض القلب إلى أبعد من 14 يوماً، لذا تعرض هذه الفترة الأطول الخطوات فقط. آخر قراءة في الأعلى.",
    latestBpm: "آخر نبض",
    export: "تصدير هذه الفترة",
    bpm: "نبضة/د",
    activityFrom: "الخطوات ونبض القلب المعروضة مصدرها {{provider}}.",
    bodyComposition: {
      title: "تركيب الجسم",
      empty: "لا توجد قياسات من ميزانك بعد.",
      takenOn: "قيس في {{date}}",
      measure: {
        weight: "الوزن",
        bmi: "مؤشر كتلة الجسم",
        fatRatio: "نسبة الدهون",
        fatMass: "كتلة الدهون",
        fatFreeMass: "الكتلة الخالية من الدهون",
        muscleMass: "الكتلة العضلية",
        hydration: "ماء الجسم",
        boneMass: "كتلة العظام",
        visceralFat: "الدهون الحشوية",
        basalMetabolicRate: "معدل الأيض الأساسي",
      },
    },
    zone: {
      light: "خفيفة",
      moderate: "متوسطة",
      vigorous: "عالية",
      peak: "قصوى",
    },
    range: {
      "24h": "آخر 24 ساعة",
      "48h": "آخر 48 ساعة",
      week: "آخر أسبوع",
      "14d": "آخر 14 يوماً",
      month: "آخر شهر",
      "90d": "آخر 90 يوماً",
    },
    outcome: {
      connected: "تم ربط الحساب.",
      denied: "لقد رفضت الطلب، فلم يتم ربط أي شيء.",
      not_linked: "لا يوجد ملف Google Health لحساب Google هذا بعد. اربط Fitbit في تطبيق Google Health ثم أعد الربط.",
      invalid_state: "انتهت صلاحية هذا الرابط. ابدأ من جديد.",
      missing_code: "لم يُرجع المزوّد أي تفويض. حاول مجدداً.",
      unavailable: "هذا التكامل غير مفعّل على هذا الخادم.",
      exchange_failed: "رفض المزوّد الاتصال. إن كنت تدير هذا الخادم، فالسبب في سجلاته.",
      account_failed: "تم تسجيل دخولك، لكن تعذّرت قراءة بياناتك. إن كنت تدير هذا الخادم، فتحقق من تفعيل واجهة المزوّد لهذا التطبيق - والسبب في السجلات.",
      store_failed: "تعذّر حفظ الاتصال. حاول مجدداً.",
    },
  },
  errors: {
    invalidHeight: "أدخل الطول بالسنتيمتر.",
    aiNotConfigured: "اربط مزوّد ذكاء اصطناعي في الإعدادات أولاً.",
    aiRefused: "رفض مزوّد الذكاء الاصطناعي الطلب. تحقق من المفتاح والنموذج والحصة.",
    invalidAiProvider: "اختر مزوّد ذكاء اصطناعي.",
    invalidAiBaseUrl: "يجب أن يكون العنوان رابط http أو https.",
    aiKeyRequired: "مفتاح API مطلوب.",
    invalidAiRole: "لا يمكن منح هذا العضو هذا الوصول.",
    sessionIncomplete: "أكمل الحصة قبل طلب تحليلها.",
    conversationTitleRequired: "أعطِ هذه المحادثة عنواناً.",
    nothingSelected: "اختر ما تريد حذفه أولاً.",
    generic: "حدث خطأ ما. حاول مرة أخرى.",
    nameOrUsername: "أدخل الاسم واللقب، أو اختر اسم مستخدم.",
    copyFailed: "تعذّر النسخ إلى الحافظة.",
    apiKeyDescription: "اذكر الغرض من هذا المفتاح.",
    invalidExpiration: "أدخل تاريخ انتهاء صالحاً.",
    configTokenRequired: "رمز مفتاح API مفقود.",
    storageNotConfigured: "أعدّ مساحة تخزين الفيديو الخاصة بك في الإعدادات قبل رفع فيديو.",
    storageUploadFailed: "رفض التخزين لديك هذا الرفع. تحقق من الحاوية وبياناتها.",
    storageDownloadFailed: "تعذّر قراءة هذا الملف من التخزين. ربما حُذف، أو تغيّرت البيانات.",
    videoTooLarge: "هذا الفيديو كبير جداً.",
    videoTooLargeMax: "هذا الفيديو كبير جدًا. الحد الأقصى {{size}}.",
    unsupportedVideo: "هذا الملف ليس فيديو يمكن للمتصفح تشغيله. استخدم MP4 أو WebM أو MOV.",
    invalidStorageType: "نوع تخزين غير معروف.",
    invalidStorageRole: "اختر قارئاً أو كاتباً.",
    invalidServiceAccount: "تعذّر قراءة مفتاح حساب الخدمة هذا.",
    eventTitleRequired: "أعطِ هذا الحدث عنواناً.",
    invalidEventDate: "تحقق من تواريخ الحدث.",
    invalidCalendarFile: "تعذّرت قراءة هذا الملف كتقويم موسم.",
    invalidBirthdate: "أدخل تاريخ ميلاد صالحاً.",
    invalidProfileVisibility: "إعداد ظهور غير معروف.",
    invitationNotFound: "لم تعد هذه الدعوة متاحة.",
    alreadyInvited: "سبق أن دُعي هذا الشخص.",
    noCoachRequest: "لا يوجد طلب تدريب معلّق لهذا الحساب.",
    rejectMotiveRequired: "اذكر سبب رفض هذا الطلب.",
    cannotMessage: "لا يمكنك مراسلة هذا العضو.",
    cannotMessageSelf: "لا يمكنك مراسلة نفسك.",
    cannotBlockSelf: "لا يمكنك حظر نفسك.",
    emptyMessage: "الرسالة تحتاج إلى نص.",
    internal: "حدث خطأ ما. حاول مرة أخرى.",
    screenTitle: "واجهت هذه الشاشة مشكلة",
    screenBody:
      "تعذّر عرض شيء في هذه الصفحة. بقية التطبيق تعمل - اختر شاشة أخرى من القائمة، أو أعد التحميل لتجربة هذه الشاشة من جديد.",
    notFound: "غير موجود.",
    invalidRequestBody: "طلب غير صالح.",
    allFieldsRequired: "املأ كل الحقول.",
    invalidCredentials: "بريد إلكتروني أو كلمة مرور خاطئة.",
    duplicateEmail: "هذا البريد مستخدم بالفعل.",
    duplicateHandle: "اسم الملف هذا محجوز.",
    invalidEmail: "أدخل بريداً إلكترونياً صالحاً.",
    deviceNotConnected: "لا يوجد حساب مرتبط بعد.",
    deviceNotLinked: "لا يوجد ملف Google Health لهذا الحساب بعد. اربط ساعتك في تطبيق Google Health.",
    deviceReauthorize: "انتهت صلاحية الاتصال. أعد ربط الحساب.",
    invalidRange: "هذه الفترة غير متاحة.",
    deviceUnavailable: "هذا التكامل غير مفعّل على هذا الخادم.",
    upstream: "تعذّر الوصول إلى المزوّد. حاول بعد قليل.",
    invalidHandle: "لا يمكن استخدام اسم الملف هذا.",
    invalidUsername: "لا يمكن استخدام اسم المستخدم هذا.",
    duplicateUsername: "اسم المستخدم هذا محجوز.",
    usernameRequired: "اختر اسم مستخدم للتصفّح باسم مستعار.",
    userNotFound: "المستخدم غير موجود.",
    noUserWithEmail: "لا حساب مسجّل بهذا البريد.",
    passwordsMismatch: "كلمتا المرور غير متطابقتين.",
    passwordTooShort: "يجب أن تتكون كلمة المرور من 8 أحرف على الأقل.",
    passwordNoUpper: "يجب أن تحتوي كلمة المرور على حرف كبير.",
    passwordNoLower: "يجب أن تحتوي كلمة المرور على حرف صغير.",
    passwordNoSymbol: "يجب أن تحتوي كلمة المرور على رمز خاص.",
    invalidToken: "هذا الرابط غير صالح أو انتهت صلاحيته.",
    invalidMfaCode: "رمز غير صالح.",
    imageTooLarge: "هذه الصورة كبيرة جداً.",
    invalidRole: "دور غير صالح.",
    cantEditOwnRole: "لا يمكنك تغيير دورك.",
    cantDeleteOwnAccount: "لا يمكنك حذف حسابك.",
    nameRequired: "أدخل اسماً.",
    cantRemoveOwner: "لا يمكن إزالة مالك النادي أو خفض دوره.",
    alreadyMember: "هذا الشخص عضو في النادي بالفعل.",
    notAClubMember: "هذا الشخص ليس عضواً في النادي.",
    duplicateExercise: "يوجد تمرين بهذا الاسم بالفعل.",
    exerciseInUse: "هذا التمرين مستخدم في برنامج ولا يمكن حذفه.",
    invalidCategory: "فئة غير صالحة.",
    invalidOneRm: "يجب أن يكون 1RM أكبر من صفر.",
    invalidSet: "هذه المجموعة غير مكتملة.",
    invalidLoadMode: "طريقة تحميل غير صالحة.",
    invalidStatus: "حالة غير صالحة.",
    uploadTooLarge: "هذا الملف كبير جداً.",
    invalidSpreadsheet: "تعذّرت قراءة هذا الملف كجدول.",
    noProgramInFile: "لم يُعثر على أي يوم تدريب في هذا الجدول.",
    emptyPost: "المنشور يحتاج إلى نص أو صورة أو رابط.",
    invalidVisibility: "إعداد ظهور غير صالح.",
    clubRequired: "اختر نادياً لمنشور خاص بنادٍ.",
    cannotLikeOwnPost: "لا يمكنك الإعجاب بمنشورك.",
    emptyComment: "التعليق يحتاج إلى نص.",
    invalidReportTarget: "بلاغ غير صالح.",
    reportReasonRequired: "اذكر سبب إبلاغك عن هذا.",
    forbidden: "لا تملك صلاحية القيام بذلك.",
    accountDisabledAdmin: "حسابك في انتظار تفعيل من الإدارة.",
    accountDisabledEmail: "أكّد حسابك عبر الرابط الذي أرسلناه إلى بريدك.",
    accountBanned: "تم حظر حسابك من قِبل الإدارة.",
    network: "تعذّر الوصول إلى الخادم. حاول مرة أخرى.",
    contactFormNotConfigured: "نموذج الاتصال غير متاح في هذه النسخة.",
    contactFormFailed: "تعذّر إرسال رسالتك. حاول لاحقاً.",
    contactRateLimited: "أرسلت رسائل كثيرة. حاول لاحقاً.",
    contactMessageTooShort: "رسالتك قصيرة جداً - أخبرنا أكثر قليلاً.",
    contactGibberish: "صُنّفت رسالتك كرسالة مزعجة. أعد صياغتها.",
  },
  about: {
    title: "حول التطبيق",
    unavailable: "لم تُكتب هذه الصفحة بعد.",
    openSource: "‏StrongFish منتج مفتوح المصدر من CWCloud. المصادر متاحة",
    openSourceLink: "هنا",
  },
  apiKeys: {
    title: "مفاتيح API",
    openApi: "مرجع API",
    intro:
      "مفتاح API يوثّق سكربتاً - أو هذا التطبيق على هاتفك - عبر ترويسة X-Api-Key بدل كلمة المرور. يحمل صلاحياتك أنت بالضبط، فتعامل معه كما تتعامل معها.",
    create: "إنشاء مفتاح",
    description: "ما الغرض منه؟",
    descriptionPlaceholder: "هاتفي، سكربت التصدير...",
    expiresAt: "ينتهي في",
    neverExpires: "لا ينتهي",
    expiresOn: "ينتهي في {{date}}",
    emptyTitle: "لا مفتاح API بعد",
    emptyBody: "أنشئ واحداً لتسجيل الدخول على هاتفك بمسح رمز QR، أو للوصول إلى API من سكربت.",
    createdTitle: "مفتاح API الجديد",
    createdWarning: "انسخه الآن: لا تُخزَّن إلا بصمته، فهذه المرة الوحيدة التي يُعرض فيها.",
    downloadConfig: "تحميل ملف الإعدادات",
    showQr: "إظهار رمز QR",
    hideQr: "إخفاء رمز QR",
    qrHelp: "امسح هذا في تطبيق الهاتف لتسجيل دخول ذلك الجهاز.",
    revoke: "إبطال",
    revokeTitle: "إبطال هذا المفتاح؟",
    revokeBody: "سيتوقف \"{{description}}\" عن العمل فوراً، في كل مكان يُستخدم فيه.",
    revoked: "تم إبطال المفتاح.",
  },
  events: {
    title: "التقويم",
    subtitle: "البطولات وحصص النادي والمعسكرات - اشترك فيه من تقويمك الخاص.",
    add: "إضافة حدث",
    edit: "تعديل الحدث",
    eventTitle: "العنوان",
    kindLabel: "النوع",
    kind: {
      competition: "منافسة",
      training: "تدريب",
      other: "أخرى",
    },
    club: "النادي",
    noClub: "تقويم مفتوح (للجميع)",
    startsAt: "يبدأ",
    endsAt: "ينتهي",
    location: "المكان",
    url: "مزيد من المعلومات",
    description: "التفاصيل",
    color: "اللون",
    allDay: "طوال اليوم",
    import: "استيراد تقويم",
    importHelp: "ارفع تقويم موسم الاتحاد - مخطط السنة بصيغة PDF أو الجدول. تُقرأ منافساته بتواريخها وتخصصاتها.",
    importing: "جارٍ قراءة التقويم…",
    imported: "تم استيراد {{count}} منافسة",
    importSkipped: "{{count}} كانت موجودة في تقويمك بالفعل",
    importWarnings: "عُثر على هذه لكنها بلا تاريخ، فأضفها يدوياً:",
    lastDay: "اليوم الأخير",
    visibility: "من يراه",
    visibilityPublic: "الجميع",
    visibilityPrivate: "أنا فقط",
    visibilityPrivateHelp: "يبقى في تقويمك وحدك. لا يراه أحد غيرك، عدا الإدارة.",
    visibilityClub: "أعضاء النادي فقط",
    showPast: "إظهار الأحداث السابقة",
    calendarLayout: "تقويم",
    listLayout: "قائمة",
    monthView: "شهر",
    weekView: "أسبوع",
    today: "اليوم",
    previous: "السابق",
    next: "التالي",
    more: "+{{count}} أخرى",
    dragHint: "اسحب داخل اليوم لاختيار وقت البداية والنهاية، أو انقر نصف ساعة.",
    moreInfo: "مزيد من المعلومات",
    emptyTitle: "لا شيء مبرمج",
    emptyBody: "لا حدث قادم. يمكن لمدرب إضافة حدث لناديه.",
    created: "تمت إضافة الحدث.",
    updated: "تم تحديث الحدث.",
    deleted: "تم حذف الحدث.",
    deleteTitle: "حذف هذا الحدث؟",
    deleteBody: "سيختفي \"{{title}}\" من تقويم الجميع، بما في ذلك الاشتراكات القائمة.",
    subscribeTitle: "الاشتراك من Outlook أو Google Calendar",
    subscribeBody:
      "أضف هذا العنوان كاشتراك تقويم لتظهر أحداثك مع بقية أسبوعك، محدَّثة تلقائياً.",
    enableFeed: "احصل على عنوان تقويمي",
    disableFeed: "إيقاف التغذية",
    copyFeed: "نسخ العنوان",
    regenerateFeed: "توليد عنوان جديد",
    feedRegenerated: "تم توليد عنوان جديد. لم يعد القديم يعمل.",
    feedWarning:
      "يستطيع أي شخص يملك هذا العنوان قراءة تقويمك - فهو بيان الاعتماد الوحيد. ولّد عنواناً جديداً إن تسرّب.",
  },
  update: {
    upToDate: "أنت على أحدث إصدار",
    available: "الإصدار {{version}} متاح",
    help: "يحمّل الإصدار الجديد ويسلّمه إلى Android للتثبيت. تبقى مسجّل الدخول.",
    install: "تحديث",
    downloading: "جارٍ تحميل التحديث...",
    failed: "تعذّر تحميل التحديث أو تثبيته. حاول لاحقاً.",
  },
  ai: {
    orderHelp: "الأول يجيب عندما لا تختار. حرّك اتصالاتك لتحديد أيها.",
    first: "يجيب افتراضياً",
    moveUp: "تحريك لأعلى",
    moveDown: "تحريك لأسفل",
    generatedBy: "تم إنشاؤه بواسطة {{connection}} في {{date}}",
    deleteFeedback: "حذف هذا التحليل",
    deleteFeedbackConfirm: "حذف هذا التحليل؟ لا يمكن استرجاعه - سيلزم طلب تحليل جديد.",
    deleteConversationConfirm: "حذف هذه المحادثة؟ سيذهب كل ما فيها معها.",
    connection: "اتصال الذكاء الاصطناعي",
    askWith: "اسأل عبر",
    modelHelp: "اختر نموذجاً من مزوّدك، أو اكتب أي اسم يقدّمه.",
    defaultConnection: "الافتراضي",
    title: "اتصالات الذكاء الاصطناعي",
    subtitle:
      "اربط نموذجاً لتسأله عن تدريبك. الحساب حسابك والمفتاح مفتاحك - ويمكنك إعارته لأعضاء آخرين، كما تعير مساحة تخزين.",
    noConnections: "لا يوجد ذكاء اصطناعي مرتبط بعد.",
    addConnection: "إضافة اتصال",
    name: "الاسم",
    namePlaceholder: "مفتاح {{provider}} الخاص بي",
    providerLabel: "المزوّد",
    baseUrl: "العنوان",
    baseUrlHelp:
      "إلى أين تذهب الطلبات. غيّره ليشير إلى بوابتك الخاصة: LiteLLM أو Ollama أو حساب OpenRouter.",
    model: "النموذج",
    token: "مفتاح API",
    tokenSet: "هناك مفتاح محفوظ بالفعل",
    tokenHelp: "مفتاح المزوّد. يُحفظ على الخادم ولا يُرسل إلى المتصفح أبداً.",
    tokenKeep: "اتركه فارغاً للاحتفاظ بالمفتاح المحفوظ.",
    saved: "تم حفظ الاتصال.",
    cleared: "تم حذف الاتصال.",
    clear: "حذف هذا الاتصال",
    shares: "من يمكنه استخدامه",
    sharesHelp:
      "أعِر اتصالك لأعضاء آخرين: يمكنهم طرح الأسئلة من خلاله فقط. لن يروا المفتاح أبداً.",
    noShares: "لا أحد آخر يستخدم هذا الاتصال.",
    shareWith: "الأعضاء",
    pickMembers: "اختر أعضاء",
    shareAction: "مشاركة",
    shared: "تمت مشاركة الاتصال.",
    unshare: "إيقاف المشاركة",
    role: { user: "يمكنه السؤال" },
    sharedBy: "مشارَك من {{name}}",
    coachTitle: "مدرب الذكاء الاصطناعي",
    coachSubtitle:
      "اسأل عن تدريبك. تأتي الإجابات من اتصال الذكاء الاصطناعي الخاص بك، وتُحفظ كل محادثة هنا.",
    connectOne: "اربط واحداً",
    newConversation: "محادثة جديدة",
    noConversations: "لا توجد محادثات بعد.",
    exchangeCount: "{{count}} تبادلات",
    exchangeOne: "تبادل واحد",
    sharedBadge: "مشارَكة",
    pickConversation: "اختر هذه المحادثة",
    deletePicked: "حذف ({{count}})",
    clearAll: "حذف الكل",
    clearAllConfirm: "حذف كل المحادثات؟ لا يمكن التراجع عن ذلك.",
    deletedCount: "تم حذف {{count}} محادثات.",
    rename: "إعادة تسمية",
    shareLink: "مشاركة رابط",
    unshareLink: "إيقاف مشاركة الرابط",
    shareLinkHelp: "يمكن لأي شخص لديه هذا الرابط قراءة المحادثة. اضغط على الرابط مجدداً لإلغائه.",
    exportConversation: "تصدير المحادثة",
    emptyTitle: "اسأل مدربك",
    emptyBody: "البرمجة والتقنية والتعافي ويوم المسابقة - اسأل أي شيء عن رفعك.",
    thinking: "جارٍ التفكير...",
    placeholder: "اطرح سؤالاً...",
    send: "إرسال",
    sharedSubtitle: "محادثة مشارَكة من StrongFish.",
  },
  storage: {
    clearConfirm: "حذف هذا التخزين؟ تبقى الملفات الموجودة فيه مكانها، لكن لن يستطيع التطبيق قراءتها.",
    serviceAccountPaste: "الصق محتوى ملف مفتاح JSON.",
    noTargets: "لا تخزين مُعدّ بعد.",
    addTarget: "إضافة تخزين",
    orderHelp: "يُرفَع الملف إلى كل الوجهات. ورابط المنشور يأتي من الأولى.",
    first: "الرابط يأتي من هنا",
    moveUp: "تحريك لأعلى",
    moveDown: "تحريك لأسفل",
    private: "هذه الحاوية ليست عامة",
    privateHelp:
      "فعّل هذا إذا كانت حاويتك ترفض الملفات العامة: عندها لا يُنشر أي ملف علناً عند الكتابة. في الحالتين، يقدّم StrongFish ملفات الحاوية بنفسه، ببيانات اعتمادك، لمن يرى ملفك الشخصي - ولمن تشاركه التخزين. أما مجلد Google Drive فمختلف: تُنشر ملفاته كروابط Drive يصل إليها كل من يملك الرابط.",
    shares: "من يمكنه استخدامه أيضاً",
    sharesHelp:
      "أعِر تخزينك لأعضاء آخرين: القارئ يشغّل ما فيه، والكاتب يرفع إليه أيضاً. أما ملفاتك أنت فتذهب دائماً إلى تخزينك أولاً.",
    noShares: "لا أحد آخر يستخدم هذا التخزين.",
    shareWith: "الأعضاء",
    pickMembers: "اختر أعضاء",
    roleLabel: "يمكنهم",
    role: {
      owner: "المالك",
      writer: "الرفع والتشغيل",
      reader: "التشغيل",
    },
    shareAction: "مشاركة",
    shared: "تمت مشاركة التخزين.",
    unshare: "إيقاف المشاركة",
    title: "تخزين الفيديو",
    subtitle:
      "لا يحتفظ StrongFish بأي فيديو، فنشر فيديو يعني استخدام تخزينك الخاص. حتى {{size}} ميغابايت لكل فيديو.",
    type: "أين تُخزَّن",
    typeS3: "حاوية متوافقة مع S3",
    typeDrive: "مجلد Google Drive",
    endpoint: "نقطة النهاية",
    region: "المنطقة",
    bucket: "الحاوية",
    accessKey: "مفتاح الوصول",
    secretKey: "المفتاح السري",
    publicBaseUrl: "العنوان العام",
    publicBaseUrlHelp: "حدّده إذا كانت الحاوية تُقدَّم عبر CDN أو نطاق خاص بك.",
    folderId: "معرّف المجلد",
    serviceAccount: "مفتاح حساب الخدمة",
    serviceAccountHelp:
      "ملف مفتاح JSON الذي تعطيك إياه Google. شارك مجلد Drive مع عنوان ذلك الحساب أولاً، وإلا فلن يستطيع الكتابة فيه.",
    serviceAccountSet: "يوجد مفتاح مُعدّ بالفعل. اختر ملفاً لاستبداله.",
    pathHelpS3: "بادئة داخل الحاوية. اتركها فارغة للكتابة في جذر الحاوية.",
    pathHelpDrive:
      "مجلد فرعي داخل مجلد Drive، يُنشأ إن لم يكن موجوداً. تداخلها بشرطات مائلة، مثل strong-fish/videos. اتركها فارغة للكتابة مباشرة في المجلد.",
    serviceAccountReadError: "تعذّرت قراءة هذا الملف. اختر مفتاح JSON الذي أعطتك إياه Google.",
    path: "مجلد فرعي",
    clear: "إزالة هذه الإعدادات",
    saved: "تم حفظ التخزين.",
    cleared: "تمت إزالة إعدادات التخزين.",
  },
  messages: {
    title: "الرسائل",
    current: "المحادثات",
    findSomeone: "ابحث عن شخص",
    record: "تسجيل رسالة صوتية",
    stopRecording: "إيقاف التسجيل",
    recordingUnsupported: "هذا المتصفح لا يستطيع تسجيل الصوت.",
    microphoneDenied: "رُفض الوصول إلى الميكروفون.",
    subtitle: "محادثات خاصة مع الأعضاء الذين تراهم.",
    message: "رسالة",
    send: "إرسال",
    placeholder: "اكتب رسالة...",
    emptyTitle: "لا محادثة بعد",
    emptyBody: "افتح ملف أحدهم وراسله لتبدأ محادثة.",
    pickTitle: "اختر محادثة",
    pickBody: "اختر شخصاً من القائمة، أو ابدأ محادثة من ملفه.",
    threadEmpty: "لا رسالة بعد. ألقِ التحية.",
    report: "الإبلاغ عن هذه الرسالة",
    reportTitle: "الإبلاغ عن هذه الرسالة",
    reportHelp: "لا يستطيع المشرفون قراءة محادثتك، لذا تُرسَل الرسالة مع بلاغك.",
    reportPlaceholder: "ما المشكلة في هذه الرسالة؟",
    reported: "تم الإبلاغ. سينظر فيه مشرف.",
    deleteTitle: "حذف هذه الرسالة؟",
    deleteBody: "ستُحذف لدى كليكما، ولا يمكن استرجاعها.",
    deleted: "تم حذف الرسالة",
  },
  blocks: {
    title: "الأعضاء المحظورون",
    subtitle: "المحظورون لا يستطيعون مراسلتك، ولا تظهر منشوراتهم في مستجداتك.",
    block: "حظر",
    blockTitle: "حظر هذا العضو؟",
    blockBody: "لن يستطيع {{name}} مراسلتك، وستختفي منشوراته من مستجداتك. يمكنك التراجع متى شئت.",
    blocked: "تم حظر العضو.",
    unblock: "رفع الحظر",
    unblocked: "تم رفع الحظر عن العضو.",
    since: "محظور منذ {{date}}",
    emptyTitle: "لا أحد محظور",
    emptyBody: "يظهر هنا الأعضاء الذين تحظرهم، لتتمكن من رفع الحظر عنهم.",
  },
  share: {
    label: "مشاركة",
    on: "المشاركة على {{network}}",
    copyLink: "نسخ الرابط",
    profileText: "{{name}} على StrongFish",
    postText: "شوهد على StrongFish",
  },
  search: {
    title: "البحث عن أشخاص",
    subtitle: "ابحث بالاسم أو اسم العائلة أو البريد. لا ترى إلا الملفات التي سمح أصحابها برؤيتها.",
    terms: "بحث",
    termsPlaceholder: "اسم، اسم مستخدم، اسم ملف، بريد إلكتروني...",
    submit: "بحث",
    advanced: "البحث حسب الحقل",
    simple: "بحث بسيط",
    clear: "مسح",
    results: "{{count}} شخصاً",
    sharesClub: "في أحد أنديتك",
    openProfile: "فتح الملف",
    startTitle: "عمّن تبحث؟",
    startBody: "اكتب اسماً أو اسم ملف أو بريداً إلكترونياً أعلاه.",
    noneTitle: "لم يُعثر على أحد",
    noneBody: "لا أحد يطابق ذلك - أو أن ملفه غير مرئي لك.",
  },
  invitations: {
    title: "الدعوات",
    subtitle: "أندية طلبت منك الانضمام إليها.",
    emptyTitle: "لا دعوة",
    emptyBody: "عندما يدعوك مدرب إلى ناديه، تظهر الدعوة هنا.",
    from: "دعاك {{name}}",
    asRole: {
      member: "ستنضم كعضو.",
      admin: "ستنضم كمشرف.",
    },
    accept: "قبول",
    decline: "رفض",
    accepted: "انضممت إلى {{club}}.",
    declined: "تم رفض الدعوة.",
  },
  publicProgram: {
    notFoundTitle: "هذا البرنامج غير متاح",
    notFoundBody: "ربما أُلغيت مشاركته، أو أن الرابط خاطئ.",
    loadsNotice:
      "هذه هي الوصفة كما كُتبت: المجموعات والتكرارات والجهد. تُحتسب الأوزان من الأرقام القصوى الخاصة بكل رياضي، فسجّل الدخول لتشغيلها على أرقامك.",
  },
  cookieBanner: {
    title: "تنبيه بشأن الكوكيز",
    message: "‏StrongFish منتج مفتوح المصدر. الكوكيز والتخزين المحلي المستخدمة ضرورية فقط لعمل التطبيق (تسجيل الدخول، المظهر واللغة) - لا للتتبّع أو التحليلات.",
    understand: "فهمت",
  },
  contact: {
    title: "اتصل بنا",
    body: "سؤال أو خلل أو ملاحظة على التطبيق؟ أرسل لنا رسالة.",
    subject: "الموضوع",
    message: "الرسالة",
    send: "إرسال الرسالة",
    sent: "تم إرسال رسالتك. شكراً لك!",
  },
  strength: {
    title: "حاسبة القوة",
    subtitle: "قيمة المجموع على المعاملات الثلاثة المعتمدة في الرياضة.",
    unit: "الوحدات",
    kg: "كغ",
    lb: "رطل",
    unitHelp: "أدخل كل وزن بالوحدة المختارة. النتائج واحدة في الحالتين.",
    division: "الفئة",
    raw: "بدون تجهيز",
    equipped: "مع تجهيز",
    bodyweight: "وزن الجسم",
    squat: "السكوات",
    bench: "ضغط البنش",
    deadlift: "الرفعة المميتة",
    total: "المجموع",
    dots: "DOTS",
    wilks: "ويلكس (1994)",
    ipfGl: "نقاط IPF GL",
    enterLifts: "أدخل وزن الجسم وحركة واحدة على الأقل لعرض النتائج.",
    tierFrom: "{{value}} DOTS",
    percentile: "تتقدم على {{value}}% من {{sample}} رافعًا هنا سجّلوا الحركات الثلاث.",
    strongerThan: "أقوى من",
    noPopulation: "لم يسجّل أحد هنا الحركات الثلاث بعد، فلا شيء للمقارنة به.",
    noBadges: "لا شارات بعد. سجّل أرقامك القصوى الثلاثة للبدء.",
    tiers: {
      novice: "مجنّد الحديد",
      intermediate: "منافس المنصة",
      advanced: "مستوى وطني",
      elite: "رافع محترف",
      worldClass: "عملاق",
    },
    badges: {
      multiplier: {
        squat2x: "سكوات بضعف وزن الجسم",
        bench1_5x: "بنش بـ1.5 ضعف وزن الجسم",
        deadlift2_5x: "رفعة مميتة بـ2.5 ضعف وزن الجسم",
        total10x: "مجموع بـ10 أضعاف وزن الجسم",
      },
      club: {
        total1000lb: "نادي 1000 رطل",
        total500kg: "نادي 500 كغ",
        total1500lb: "نادي 1500 رطل",
      },
      dots: {
        300: "نادي 300 DOTS",
        400: "نادي 400 DOTS",
        500: "نادي 500 DOTS",
      },
      style: {
        squatDominant: "متخصص السكوات",
        deadliftDominant: "متخصص الرفعة المميتة",
      },
    },
  },
};

export default ar;
